// src/private/src/pages/app/clients/components/MobileMetricCards.jsx
import React, { memo } from 'react';
import { Box, Card, CardContent, Divider, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DollarFormatter, PercentageFormatter } from '@src/components';
import { getMetricCellSx } from './MetricCell';

const safeDiv = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb) || nb === 0) return 0;
  return na / nb;
};

const MetricRow = ({ label, value, format, metricKey }) => {
  const tierSx = metricKey ? getMetricCellSx(metricKey, value) : {};
  const display =
    format === 'dollar' ? <DollarFormatter value={value} /> :
    format === 'percent' ? <PercentageFormatter value={value} /> :
    (Number(value) || 0).toLocaleString('en-US');

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.5, px: 1 }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, fontSize: '0.8rem', ...tierSx }}
      >
        {display}
      </Typography>
    </Stack>
  );
};

/**
 * Mobile-friendly card layout that replaces wide tables on small screens.
 * Each row from the table becomes a card with vertically stacked metrics.
 *
 * @param {Array} rows - Array of data objects (same shape as table rows)
 * @param {string} labelKey - Key to use as the card title (e.g., 'vertical', 'channel__name')
 * @param {Function} getLabel - Optional function to extract label from row
 * @param {boolean} showFee - Whether to include fees in spend
 * @param {Function} getSpendWithFees - Function to calculate spend with fees
 */
function MobileMetricCards({
  rows = [],
  labelKey = 'vertical',
  getLabel,
  showFee = false,
  getSpendWithFees,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) return null;

  return (
    <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
      {rows.map((row, idx) => {
        const label = getLabel
          ? getLabel(row)
          : (row[labelKey] || '').trim() || 'Unspecified';
        const spend = showFee && getSpendWithFees
          ? getSpendWithFees(row)
          : (row.total_spend_sum || 0);
        const leads = row.total_leads_sum || 0;
        const booked = row.booked_sum || 0;
        const issues = row.issues_sum || 0;
        const scheduled = row.scheduled_sum || 0;
        const demos = row.demos_sum || 0;
        const netSold = row.net_sales_sum || row.net_sold_sum || 0;
        const netAmount = row.net_amount_sum || 0;

        return (
          <Card
            key={row.uuid || `${label}-${idx}`}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                {label}
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
              <MetricRow label="Spend" value={spend} format="dollar" />
              <MetricRow label="Leads" value={leads} format="number" />
              <MetricRow label="CPL" value={safeDiv(spend, leads)} format="dollar" />
              <MetricRow label="Booked" value={booked} format="number" />
              <MetricRow label="Set Rate" value={safeDiv(booked, leads)} format="percent" metricKey="setRate" />
              <MetricRow label="Issues" value={issues} format="number" />
              <MetricRow label="Issue Rate" value={safeDiv(issues, scheduled)} format="percent" metricKey="issueRate" />
              <MetricRow label="Demos" value={demos} format="number" />
              <MetricRow label="Demo Rate" value={safeDiv(demos, issues)} format="percent" metricKey="demoRate" />
              <MetricRow label="Net Sold" value={netSold} format="number" />
              <MetricRow label="Net Close Rate" value={safeDiv(netSold, demos)} format="percent" metricKey="netCloseRate" />
              <MetricRow label="Net Amount" value={netAmount} format="dollar" />
              {spend > 0 && netAmount > 0 && (
                <MetricRow label="COM" value={safeDiv(spend, netAmount)} format="percent" metricKey="costOfMarketing" />
              )}
            </CardContent>
          </Card>
        );
      })}
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No data available for the selected range.
        </Typography>
      )}
    </Stack>
  );
}

export default memo(MobileMetricCards);
